import React, { useState } from 'react';
import DspxModal from '../../../../../common/DspxModal/DspxModal';
import { Button, Form, message, Upload } from 'antd';
import { TenantAutoInfoFields } from '../TenantAutoInfoFields/TenantAutoInfoFields';
import { useMutation, useQueryClient } from 'react-query';
import addTransport from '../../../../../../requests/addTransport';
import changeTransport from '../../../../../../requests/changeTransport';
import { useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import { getQueryKeys } from '../../../../../../queries/queryKeys';
import { maxFileSize } from '../../../../../../../../constants/maxFileSize';
import { documentsAllowedExtensions } from '../../../../../../constants/documentsAllowedExtensions';
import { useAddTransportAttachments } from '../../../../../../requests/addTransportAttachments';
import styles from './TenantAutoInfoModal.module.scss';

const beforeUpload = (file) => {
  const fileExtension = file.name.split('.').pop().toLowerCase();

  if (!documentsAllowedExtensions.includes(fileExtension)) {
    message.error(`Файл ${file.name} имеет недопустимый формат.`);
    return false;
  }

  if (file.size > maxFileSize) {
    message.error(`Файл ${file.name} весит больше 15 мб.`);
    return false;
  }

  return true;
};

const TenantAutoInfoNode = ({
  clientId,
  addressId,
  form,
  initialValues,
  onCancel,
  ...props
}) => {
  const { resetFields, validateFieldsAndScroll } = form;

  const match = useRouteMatch();

  const queryClient = useQueryClient();

  const addTransportMutation = useMutation(
    ({ values }) => addTransport(values),
    {
      onSuccess: () => {
        resetFields();
        onCancel();
      },
    }
  );

  const changeTransportMutation = useMutation(
    ({ values, id }) => changeTransport(values, { id }),
    {
      onSuccess: () => {
        resetFields();
        onCancel();
      },
    }
  );

  const { addAttachments, loading: addAttachmentsLoading } =
    useAddTransportAttachments();

  const [attachments, setAttachments] = useState([]);

  const handleAddAttachments = ({ file }) => {
    setAttachments((prev) => [
      ...prev,
      { originFileObj: file, fileType: 'file' },
    ]);
  };

  const handleRemoveAttachment = (file) => {
    setAttachments((prev) =>
      prev.filter((f) => f.originFileObj.uid !== file.uid)
    );
  };

  const handleSubmit = () => {
    try {
      validateFieldsAndScroll(async (err, fieldValues) => {
        if (!err) {
          const { country, number, region, model, brand } = fieldValues;

          const upperNumber = number.toUpperCase();
          const houseId = Number(match.params.pageId);
          const companyId = Number(match.params.companyId);

          const values = {
            clientId,
            addressId,
            countryId: country,
            number: upperNumber,
            region,
            model,
            brand,
          };

          if (initialValues) {
            await changeTransportMutation.mutateAsync({
              values,
              id: initialValues.id,
            });
          } else {
            const { id } = await addTransportMutation.mutateAsync({ values });
            if (id && !!attachments.length) {
              await addAttachments({ transportId: id, files: attachments });
            }

            resetFields();
          }

          queryClient.invalidateQueries(getQueryKeys.flatsGrid(houseId));
          queryClient.invalidateQueries(getQueryKeys.housingStock(companyId));
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <DspxModal
      title={initialValues ? 'Редактировать авто' : 'Добавить новое авто'}
      okText={initialValues ? 'Сохранить' : 'Добавить'}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={
        addTransportMutation.isLoading || changeTransportMutation.isLoading
      }
      destroyOnClose
      {...props}
    >
      <TenantAutoInfoFields
        autoInfo={initialValues}
        form={form}
        isCanEdit
      />
      {!initialValues && (
        <Upload
          className={styles.uploadWrapper}
          customRequest={({ file, onSuccess }) => {
            handleAddAttachments({ file });
            setTimeout(() => {
              onSuccess('ok');
            }, 0);
          }}
          multiple
          showUploadList
          beforeUpload={beforeUpload}
          onRemove={handleRemoveAttachment}
        >
          <Button
            className={styles.uploadButton}
            type="dashed"
          >
            Перетащите/выберите одно или несколько вложений
          </Button>
        </Upload>
      )}
    </DspxModal>
  );
};

export const TenantAutoInfoModal = Form.create({ name: 'tenant_auto_info' })(
  TenantAutoInfoNode
);
